import { useId, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { dismissCandidate, proposeCandidate, resolveCandidate, type CandidateView, type ResolutionKind } from "../../../api/content/curationApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import TermSearch from "../../ontology/browse/TermSearch";
import { isTypingTarget } from "../state/keyboard";
import { initialDraft, percent, resolutionRequest, withTarget, type ResolutionDraft } from "./candidateModel";
import CompareFields from "./CompareFields";
import ResolvedCandidate from "./ResolvedCandidate";
import Chip from "../../../ui/feedback/Chip";
import FieldGroup from "../../../ui/forms/FieldGroup";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { classNames } from "../../../ui/styles/classNames";
import Field from "../../../ui/forms/Field";
import { useWorkspace } from "../../../shell/workspace";

const KINDS: readonly ResolutionKind[] = ["keep_new", "merge_into", "drop_new"];

/**
 * Possible duplicates of one new term (curation-ui.md CU-6): each target with its score
 * breakdown and evidence, the two terms side by side, and v1's choice in DMW form: keep
 * new (as a narrower term, or in another module), merge into the target with a choice per
 * field, or drop new. Applying it is one commit on the head shown.
 */
export default function CandidateBlock({
  views,
  modules,
  head,
  changeSetId,
  projectId,
  termIri,
  termName,
  labels,
  canEdit,
  canPropose,
  onChanged,
}: {
  views: readonly CandidateView[];
  modules: readonly { readonly itemId: string; readonly name: string }[];
  head: string;
  changeSetId: string;
  projectId: string;
  termIri: string;
  termName: string;
  labels: Readonly<Record<string, string>>;
  canEdit: boolean;
  /** Proposing a possible duplicate needs only COMMENT (module B's `propose_candidate`). */
  canPropose: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const submission = useSubmission();
  const open = views.filter((view) => view.candidate.state === "open");
  const closed = views.filter((view) => view.candidate.state !== "open");
  const first = open[0];
  const [draft, setDraft] = useState<ResolutionDraft | null>(first === undefined ? null : initialDraft(first));
  const [dismissing, setDismissing] = useState(false);
  const [proposing, setProposing] = useState(false);
  const busy = submission.state.status === "submitting";
  const chosen = open.find((view) => view.candidate.candidateId === draft?.candidateId) ?? first;

  // Only the tab worked in answers the number keys (F90).
  const { isActive } = useWorkspace();
  useEffect(() => {
    if (draft === null || !canEdit || !isActive) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      const kind = KINDS[Number(event.key) - 1];
      if (kind !== undefined && !isTypingTarget(event.target) && !event.ctrlKey && !event.metaKey && !event.altKey) {
        setDraft({ ...draft, kind });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [draft, canEdit, isActive]);

  // As the rest of the review names a term (P-E): its names in the reader's language first, then the summary's label
  // (English or untagged, from the server), then that it has none, never its IRI (P-F).
  const targetName = (view: CandidateView) => labels[view.candidate.target] ?? view.targetSummary?.label ?? t("reviews.unnamedTerm");
  const apply = () => {
    if (draft === null) {
      return;
    }
    submission.submit(async () => {
      await resolveCandidate(changeSetId, draft.candidateId, resolutionRequest(draft, head));
      onChanged();
    });
  };

  return (
    <section className="space-y-3" aria-labelledby={headingId}>
      <h4 id={headingId} className="app-subsection-title text-sm">
        {open.length > 0 ? t("reviews.curation.candidates.title", { count: open.length }) : t("reviews.curation.candidates.none")}
      </h4>
      {open.length > 0 && draft !== null && chosen !== undefined ? (
        <div className="app-card space-y-3 p-3">
          <FieldGroup legend={t("reviews.curation.candidates.target")} className="space-y-1">
            {open.map((view) => (
              <OptionLabel key={view.candidate.candidateId} className={classNames("flex-wrap items-baseline rounded px-2 py-1", view === chosen && "app-card-muted")}>
                <input
                  type="radio"
                  name={`target-${termIri}`}
                  checked={view === chosen}
                  onChange={() => {
                    setDraft(withTarget(draft, view));
                  }}
                />
                <span className="font-semibold">{targetName(view)}</span>
                <Chip>{percent(view.candidate.scores.total)}</Chip>
                <ScoreBreakdown view={view} />
                {view.suggestsMerge ? <Chip tone="warning">{t("reviews.curation.candidates.suggestsMerge")}</Chip> : null}
                {view.targetSummary !== null ? <span className="app-text-muted text-xs">{t("reviews.curation.candidates.inModule", { name: view.targetSummary.moduleName })}</span> : null}
              </OptionLabel>
            ))}
          </FieldGroup>
          <ul className="app-text-muted list-disc space-y-0.5 pl-5 text-xs">
            {chosen.candidate.evidence.map((evidence) => (
              <li key={`${evidence.kind}:${evidence.text}`}>
                <span className="font-medium">{t(`reviews.curation.evidence.${evidence.kind}`)}:</span> {evidence.text}
              </li>
            ))}
            {chosen.candidate.sources.map((source) => (
              <li key={`${source.kind}:${source.threadEntry ?? source.person ?? ""}`}>{t(`reviews.curation.sources.${source.kind}`)}</li>
            ))}
          </ul>
          <CompareFields
            fields={chosen.fields}
            choices={draft.kind === "merge_into" ? draft.fieldChoices : null}
            onChoose={(predicate, action) => {
              setDraft({ ...draft, fieldChoices: { ...draft.fieldChoices, [predicate]: action } });
            }}
            termName={termName}
            targetName={targetName(chosen)}
            labels={labels}
          />
          {canEdit ? (
            <div className="space-y-2">
              <div role="radiogroup" aria-label={t("reviews.curation.candidates.resolution")} className="flex flex-wrap gap-2">
                {KINDS.map((kind, index) => (
                  <AppButton
                    key={kind}
                    size="sm"
                    variant={draft.kind === kind ? "primary" : "secondary"}
                    role="radio"
                    aria-checked={draft.kind === kind}
                    aria-keyshortcuts={String(index + 1)}
                    onClick={() => {
                      setDraft({ ...draft, kind });
                    }}
                  >
                    {t(`reviews.curation.resolutions.${kind}`, { target: targetName(chosen) })}
                  </AppButton>
                ))}
              </div>
              {draft.kind === "keep_new" ? (
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <OptionLabel>
                    <input
                      type="checkbox"
                      checked={draft.narrower}
                      onChange={(event) => {
                        setDraft({ ...draft, narrower: event.target.checked });
                      }}
                    />
                    {t("reviews.curation.keep.narrower", { target: targetName(chosen) })}
                  </OptionLabel>
                  <Field layout="inline" label={t("reviews.curation.keep.module")} className="gap-1">
                    {(control) => (
                      <select {...control}
                        className={FORM_SELECT_CLASS}
                        value={draft.module ?? ""}
                        onChange={(event) => {
                          setDraft({ ...draft, module: event.target.value === "" ? null : event.target.value });
                        }}
                      >
                        <option value="">{t("reviews.curation.keep.moduleUnchanged", { name: chosen.termSummary?.moduleName ?? "" })}</option>
                        {modules
                          .filter((module) => module.itemId !== chosen.termSummary?.module)
                          .map((module) => (
                            <option key={module.itemId} value={module.itemId}>
                              {module.name}
                            </option>
                          ))}
                      </select>
                    )}
                  </Field>
                </div>
              ) : null}
              <p className="app-text-muted text-xs">{t(`reviews.curation.explain.${draft.kind}`, { target: targetName(chosen), term: termName })}</p>
              <input
                className={FORM_COMPACT_CONTROL_CLASS}
                aria-label={t("reviews.curation.candidates.reason")}
                placeholder={t("reviews.curation.candidates.reason")}
                value={draft.reason}
                maxLength={2000}
                onChange={(event) => {
                  setDraft({ ...draft, reason: event.target.value });
                }}
              />
              <div className="flex flex-wrap gap-2">
                <AppButton size="sm" variant="primary" disabled={busy} onClick={apply}>
                  {t("reviews.curation.candidates.apply")}
                </AppButton>
                <AppButton
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    setDismissing(!dismissing);
                  }}
                >
                  {t("reviews.curation.candidates.dismiss")}
                </AppButton>
              </div>
              {dismissing ? (
                <DismissForm
                  busy={busy}
                  onSend={(reason) => {
                    submission.submit(async () => {
                      await dismissCandidate(changeSetId, chosen.candidate.candidateId, reason);
                      setDismissing(false);
                      onChanged();
                    });
                  }}
                />
              ) : null}
            </div>
          ) : (
            <p className="app-text-muted text-xs">{t("reviews.curation.candidates.readOnly")}</p>
          )}
        </div>
      ) : null}
      {closed.map((view) => (
        <ResolvedCandidate key={view.candidate.candidateId} view={view} targetName={targetName(view)} labels={labels} projectId={projectId} />
      ))}
      {canPropose ? (
        <div className="space-y-1">
          <AppButton
            size="sm"
            variant="ghost"
            onClick={() => {
              setProposing(!proposing);
            }}
          >
            {t("reviews.curation.candidates.propose")}
          </AppButton>
          {proposing ? (
            <TermSearch
              projectId={projectId}
              commit={head}
              onPick={(term) => {
                submission.submit(async () => {
                  await proposeCandidate(changeSetId, { head, term: termIri, target: term.iri, rationale: "" });
                  setProposing(false);
                  onChanged();
                });
              }}
            />
          ) : null}
        </div>
      ) : null}
      {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} onRetry={onChanged} compact={true} /> : null}
    </section>
  );
}

function ScoreBreakdown({ view }: { view: CandidateView }) {
  const { t } = useTranslation();
  const scores = view.candidate.scores;
  const parts = (["lexical", "semantic", "structural"] as const).filter((kind) => scores[kind] !== null);
  return (
    <span className="app-text-muted text-xs">
      {parts.map((kind) => t(`reviews.curation.scores.${kind}`, { value: percent(scores[kind]) })).join(" · ")}
    </span>
  );
}

function DismissForm({ busy, onSend }: { busy: boolean; onSend: (reason: string) => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  return (
    <div className="flex flex-wrap gap-2">
      <input
        className={FORM_COMPACT_CONTROL_CLASS}
        aria-label={t("reviews.curation.candidates.dismissReason")}
        placeholder={t("reviews.curation.candidates.dismissReason")}
        value={reason}
        maxLength={2000}
        onChange={(event) => {
          setReason(event.target.value);
        }}
      />
      <AppButton
        size="sm"
        disabled={busy || reason.trim() === ""}
        onClick={() => {
          onSend(reason.trim());
        }}
      >
        {t("reviews.curation.candidates.dismissSend")}
      </AppButton>
    </div>
  );
}
